/**
 * Touch controls as held state: the phone's equivalent of the pad (`docs/mobile.md`).
 *
 * A touch starting inside the stationary left stick drives the legs: the thumb's offset from its
 * visible center is the left stick (run, and with stick actions up jumps and down tucks). Only the
 * knob moves, clamped to the rim; the base never follows a touch. The right side is
 * the body: a touch holds the button it lands on (GRAB, JUMP, or TUCK) until it lifts, sliding onto
 * GRAB grabs as well, and every right-side touch leans the torso by how far it has slid sideways
 * from where it landed. Lifting a finger lets go of whatever it held, which is how a bar is released.
 *
 * The reader knows nothing of the DOM: the app feeds it pointer positions in CSS pixels and the
 * button circles from its layout, and `InputSource` reads it once per tick. A press that starts and
 * ends between two reads still counts once, so a quick tap is never lost between ticks.
 */

export type TouchButton = "grab" | "jump" | "tuck";

/** Taps with no body meaning: the app's pause and restart buttons, read like Start and Back. */
export type TouchSystemButton = "pause" | "restart";

export interface TouchConfig {
  /** Knob travel for a full stick deflection, CSS px. */
  stickRadius: number;
  /** Stick deadzone, as a fraction of the radius. */
  stickDeadzone: number;
  /**
   * The stick jumps or tucks (past the stick action threshold) only within this many degrees of
   * straight up or down: a thumb swinging to run drifts on a diagonal, and a drift must not jump.
   */
  stickActionDeg: number;
  /** Sideways slide for a full lean, CSS px. */
  leanRadius: number;
  /** A slide shorter than this does not lean, CSS px, so a tap that wobbles does not tilt the body. */
  leanDeadzone: number;
  /** A button takes touches this far outside its drawn circle, as a fraction of its radius. */
  buttonSlop: number;
  /** Left-side boundary: only contacts inside the visible stick drive it; others are ignored. */
  stickZone: number;
  /**
   * In the air with GRAB held and the thumb not leaning, the stick's forward push leans the body
   * forward (the dive that puts the hands ahead for the next bar): a pad has a right stick for it,
   * a phone's right thumb is busy holding GRAB. Off, the lean is only the slide.
   */
  autoDive: boolean;
  /** Help touch laches with flight posture and a continuous GRAB-to-JUMP release gesture. */
  lacheAssist: boolean;
  /** Stick intent at which its swing direction takes priority over incidental grip-thumb motion. */
  pumpIntentThreshold: number;
}

export const DEFAULT_TOUCH_CONFIG: TouchConfig = {
  stickRadius: 60,
  stickDeadzone: 0.12,
  stickActionDeg: 35,
  leanRadius: 64,
  leanDeadzone: 10,
  autoDive: true,
  lacheAssist: true,
  pumpIntentThreshold: 0.25,
  buttonSlop: 0.3,
  stickZone: 0.45,
};

/** What the touch controls hold at one read. */
export interface TouchFrame {
  /** A finger is down, or a tap has not been read yet. */
  active: boolean;
  /** The stick, -1..1, up is positive. */
  moveX: number;
  moveY: number;
  /** The lean, -1..1, positive toward +x. */
  lean: number;
  /** 1 while GRAB is held. */
  reach: number;
  jump: boolean;
  tuck: boolean;
  pause: boolean;
  restart: boolean;
}

export function createTouchFrame(): TouchFrame {
  return {
    active: false,
    moveX: 0,
    moveY: 0,
    lean: 0,
    reach: 0,
    jump: false,
    tuck: false,
    pause: false,
    restart: false,
  };
}

export interface TouchCircle {
  x: number;
  y: number;
  /** Drawn radius, CSS px; 0 takes no touches. */
  r: number;
}

const BUTTONS: readonly TouchButton[] = ["grab", "jump", "tuck"];
const GRAB = 0;
const JUMP = 1;
const TUCK = 2;
const SLOTS = 10;
const FREE = 0;
const STICK = 1;
const BODY = 2;
/** A left-side contact outside the stick, or a second thumb: ignored until its lift. */
const IGNORED = 3;

function buttonIndex(b: TouchButton): number {
  return b === "grab" ? GRAB : b === "jump" ? JUMP : TUCK;
}

export class TouchReader {
  readonly config: TouchConfig;
  /** Screen size, CSS px. */
  width = 0;
  height = 0;
  /** Button circles in CSS px, from the app's layout. */
  readonly buttons: Record<TouchButton, TouchCircle> = {
    grab: { x: 0, y: 0, r: 0 },
    jump: { x: 0, y: 0, r: 0 },
    tuck: { x: 0, y: 0, r: 0 },
  };
  /** The stick, for drawing: whether a finger holds it, its center, and the knob's offset, CSS px. */
  readonly stick = { held: false, cx: 0, cy: 0, dx: 0, dy: 0 };
  /** performance.now() of the most recent button press, for the latency probe. */
  lastEdgeTime = 0;

  private readonly ids = new Float64Array(SLOTS);
  private readonly kind = new Uint8Array(SLOTS);
  /** The button a body touch landed on, or -1. */
  private readonly held = new Int8Array(SLOTS);
  /** A body touch that slid onto GRAB. */
  private readonly grabbed = new Uint8Array(SLOTS);
  /** Crossing JUMP releases once; holding there through the next catch cannot release again. */
  private readonly jumpZone = new Uint8Array(SLOTS);
  /** Button travel, not torso lean: 1 returning to GRAB; 2 approaching its center; 3 approaching TUCK. */
  private readonly returning = new Uint8Array(SLOTS);
  /** Ladder slide: 0 armed, 1 holding TUCK, 2 left TUCK and waiting to return to GRAB. */
  private readonly ladderTuck = new Uint8Array(SLOTS);
  /** Separate from ordinary TUCK taps so leaving a ladder cancels only its unread gesture. */
  private pendingLadderTuck = false;
  private onBar = false;
  private onLadder = false;
  /** Anchor (the stick's center, or where a lean is measured from) and the finger, CSS px. */
  private readonly ax = new Float64Array(SLOTS);
  private readonly ay = new Float64Array(SLOTS);
  private readonly px = new Float64Array(SLOTS);
  private readonly py = new Float64Array(SLOTS);
  private stickSlot = -1;
  /** Presses not read yet, per body button, then pause and restart. */
  private readonly pending = new Uint8Array(5);

  constructor(config: Partial<TouchConfig> = {}) {
    this.config = { ...DEFAULT_TOUCH_CONFIG, ...config };
  }

  /** The app's visible stick center in logical CSS pixels. Layout changes release old gestures. */
  setStickCenter(x: number, y: number): void {
    if (this.stick.cx === x && this.stick.cy === y) return;
    this.cancelAll();
    this.stick.cx = x;
    this.stick.cy = y;
  }

  /** The button whose circle (with slop) holds the point, nearest first, or -1. */
  private hit(x: number, y: number): number {
    let best = -1;
    let bestRatio = Number.POSITIVE_INFINITY;
    for (let i = 0; i < BUTTONS.length; i++) {
      const c = this.buttons[BUTTONS[i]!];
      if (c.r <= 0) continue;
      const ratio = Math.hypot(x - c.x, y - c.y) / c.r;
      if (ratio <= 1 + this.config.buttonSlop && ratio < bestRatio) {
        best = i;
        bestRatio = ratio;
      }
    }
    return best;
  }

  private slotOf(id: number): number {
    for (let i = 0; i < SLOTS; i++) if (this.kind[i] !== FREE && this.ids[i] === id) return i;
    return -1;
  }

  /** A finger landed at (x, y). Returns what it took: a body button, the stick, the lean alone, or nothing. */
  down(id: number, x: number, y: number, now: number): TouchButton | "stick" | "lean" | null {
    if (this.slotOf(id) >= 0) this.up(id);
    let slot = -1;
    for (let i = 0; i < SLOTS; i++) {
      if (this.kind[i] === FREE) {
        slot = i;
        break;
      }
    }
    if (slot < 0) return null;
    this.ids[slot] = id;
    this.px[slot] = x;
    this.py[slot] = y;
    this.grabbed[slot] = 0;
    this.jumpZone[slot] = 0;
    this.returning[slot] = 0;
    this.ladderTuck[slot] = 0;
    this.held[slot] = -1;
    const b = this.hit(x, y);
    if (b < 0 && x < this.width * this.config.stickZone) {
      if (this.stickSlot >= 0 || Math.hypot(x - this.stick.cx, y - this.stick.cy) > this.config.stickRadius) {
        this.kind[slot] = IGNORED;
        return null;
      }
      this.kind[slot] = STICK;
      this.stickSlot = slot;
      this.ax[slot] = this.stick.cx;
      this.ay[slot] = this.stick.cy;
      this.updateStick(slot);
      return "stick";
    }
    this.kind[slot] = BODY;
    this.ax[slot] = x;
    this.ay[slot] = y;
    if (b < 0) return "lean";
    this.held[slot] = b;
    this.pending[b] = 1;
    this.lastEdgeTime = now;
    return BUTTONS[b]!;
  }

  /** A finger moved. */
  move(id: number, x: number, y: number, now?: number): void {
    const slot = this.slotOf(id);
    if (slot < 0) return;
    const previousX = this.px[slot]!;
    const previousY = this.py[slot]!;
    this.px[slot] = x;
    this.py[slot] = y;
    if (this.kind[slot] === STICK) {
      this.updateStick(slot);
    } else if (this.kind[slot] === BODY) {
      const L = this.config.leanRadius;
      const off = x - this.ax[slot]!;
      if (off > L) this.ax[slot] = x - L;
      else if (off < -L) this.ax[slot] = x + L;
      const hit = this.hit(x, y);
      if (this.held[slot] !== GRAB && this.grabbed[slot] === 0 && hit === GRAB) {
        this.grabbed[slot] = 1;
        // Travelling between buttons is not a backward dive. A free lean that acquires GRAB
        // keeps its deliberate pose; a JUMP/TUCK button handoff gets a fresh lean anchor.
        if (this.held[slot]! >= 0) {
          this.ax[slot] = x;
          this.ay[slot] = y;
          this.returning[slot] = 2;
        }
      }
      const gripping = this.held[slot] === GRAB || this.grabbed[slot] === 1;
      if (
        this.config.lacheAssist &&
        this.onBar &&
        !this.onLadder &&
        gripping &&
        this.held[slot] !== JUMP &&
        hit === JUMP &&
        this.returning[slot] !== 1 &&
        this.jumpZone[slot] === 0
      ) {
        this.pending[JUMP] = 1;
        this.returning[slot] = 1;
        this.ax[slot] = x;
        this.ay[slot] = y;
        if (now !== undefined) this.lastEdgeTime = now;
      }
      if (this.onBar && this.onLadder && gripping && this.held[slot] !== TUCK) {
        if (hit === TUCK && this.returning[slot] !== 1) {
          this.ladderTuck[slot] = 1;
          this.pendingLadderTuck = true;
          this.returning[slot] = 1;
          this.ax[slot] = x;
          this.ay[slot] = y;
          if (now !== undefined) this.lastEdgeTime = now;
        } else if (this.ladderTuck[slot] === 1 && hit !== TUCK) {
          // Leaving the circle releases TUCK, but only GRAB re-arms it. Edge jitter cannot
          // turn a held swipe into several fresh ducks.
          this.ladderTuck[slot] = 2;
        }
        if (this.returning[slot] !== 1 && hit !== TUCK) {
          const tuck = this.buttons.tuck;
          const dx = x - previousX;
          const dy = y - previousY;
          const tx = tuck.x - previousX;
          const ty = tuck.y - previousY;
          const forward = dx * tx + dy * ty;
          const towardTuck = tuck.r > 0 && forward > Math.abs(dx * ty - dy * tx);
          // A slide aimed at TUCK is button travel from its first move, before entering
          // the circle. Horizontal thumb pumping remains available on the ladder.
          if (towardTuck) this.returning[slot] = 3;
          if (this.returning[slot] === 3) {
            this.ax[slot] = x;
            this.ay[slot] = y;
            const grab = this.buttons.grab;
            if (
              hit === GRAB &&
              Math.hypot(x - grab.x, y - grab.y) < Math.hypot(previousX - grab.x, previousY - grab.y)
            ) {
              this.returning[slot] = 2;
            } else if (!towardTuck && (dx !== 0 || dy !== 0)) {
              // Abandoning the slide restores deliberate thumb pumping from its last point.
              this.returning[slot] = 0;
              this.ax[slot] = previousX;
              this.ay[slot] = previousY;
            }
          }
        }
      }
      if (this.returning[slot] === 1 && hit === GRAB) {
        this.returning[slot] = 2;
        this.ladderTuck[slot] = 0;
        this.ax[slot] = x;
        this.ay[slot] = y;
      }
      if (this.returning[slot] === 2) {
        const grab = this.buttons.grab;
        // Entering the slop circle is not the end of the gesture. Follow the rest of the
        // centerward travel; moving away again restores deliberate lean from that resting point.
        if (Math.hypot(x - grab.x, y - grab.y) <= Math.hypot(previousX - grab.x, previousY - grab.y)) {
          this.ax[slot] = x;
          this.ay[slot] = y;
        } else {
          this.returning[slot] = 0;
        }
      }
      this.jumpZone[slot] = hit === JUMP ? 1 : 0;
    }
  }

  /** A JUMP-to-GRAB slide keeps a full ground jump, then frees Jump as soon as the bar is caught. */
  setBarContext(onBar: boolean, onLadder = false): void {
    if (this.onBar && this.onLadder && !(onBar && onLadder)) {
      this.pendingLadderTuck = false;
      for (let i = 0; i < SLOTS; i++) {
        if (this.kind[i] !== BODY || (this.ladderTuck[i] === 0 && this.returning[i] !== 3)) continue;
        this.ladderTuck[i] = 0;
        this.returning[i] = 0;
        this.ax[i] = this.px[i]!;
        this.ay[i] = this.py[i]!;
      }
    }
    this.onBar = onBar;
    this.onLadder = onLadder;
    if (!onBar) return;
    for (let i = 0; i < SLOTS; i++) {
      if (this.kind[i] === BODY && this.held[i] === JUMP && this.grabbed[i] === 1) {
        this.held[i] = GRAB;
        this.grabbed[i] = 0;
      }
    }
  }

  /** A finger lifted (or the system took it). */
  up(id: number): void {
    const slot = this.slotOf(id);
    if (slot < 0) return;
    this.kind[slot] = FREE;
    this.held[slot] = -1;
    this.grabbed[slot] = 0;
    this.jumpZone[slot] = 0;
    this.returning[slot] = 0;
    this.ladderTuck[slot] = 0;
    if (slot === this.stickSlot) {
      this.stickSlot = -1;
      this.stick.held = false;
      this.stick.dx = 0;
      this.stick.dy = 0;
    }
  }

  /** Every finger lifts: a menu opened, the page lost focus, or the controls hid. Unread taps are dropped too. */
  cancelAll(): void {
    for (let i = 0; i < SLOTS; i++) {
      this.kind[i] = FREE;
      this.held[i] = -1;
      this.grabbed[i] = 0;
      this.jumpZone[i] = 0;
      this.returning[i] = 0;
      this.ladderTuck[i] = 0;
    }
    this.stickSlot = -1;
    this.stick.held = false;
    this.stick.dx = 0;
    this.stick.dy = 0;
    this.pending.fill(0);
    this.pendingLadderTuck = false;
  }

  /** Pause or restart from the app's buttons: counts as one press at the next read. */
  tap(button: TouchSystemButton, now: number): void {
    this.pending[button === "pause" ? 3 : 4] = 1;
    this.lastEdgeTime = now;
  }

  /** Whether a body button is down, for drawing. */
  isHeld(button: TouchButton): boolean {
    const b = buttonIndex(button);
    for (let i = 0; i < SLOTS; i++) {
      if (this.kind[i] !== BODY) continue;
      if (this.held[i] === b || (b === GRAB && this.grabbed[i] === 1)) return true;
      if (b === JUMP && this.returning[i] === 1 && this.jumpZone[i] === 1 && this.ladderTuck[i] === 0)
        return true;
      if (b === TUCK && this.ladderTuck[i] === 1) return true;
    }
    return false;
  }

  /** Whether any finger is down. */
  get touching(): boolean {
    for (let i = 0; i < SLOTS; i++) if (this.kind[i] !== FREE) return true;
    return false;
  }

  /** A slide release or ladder duck keeps GRAB held; return to GRAB before repeating it. */
  get awaitingGripReturn(): boolean {
    for (let i = 0; i < SLOTS; i++) {
      if (this.kind[i] === BODY && this.returning[i] === 1) return true;
    }
    return false;
  }

  /** Publish the knob offset, clamped to the rim without moving the fixed center. */
  private updateStick(slot: number): void {
    const R = this.config.stickRadius;
    let dx = this.px[slot]! - this.ax[slot]!;
    let dy = this.py[slot]! - this.ay[slot]!;
    const m = Math.hypot(dx, dy);
    if (m > R) {
      dx *= R / m;
      dy *= R / m;
    }
    this.stick.held = true;
    this.stick.dx = dx;
    this.stick.dy = dy;
  }

  /** Write what the controls hold into `out` and consume the unread presses. Allocates nothing. */
  read(out: TouchFrame): void {
    const cfg = this.config;
    let active = false;
    let jump = this.pending[JUMP] === 1;
    let tuck = this.pending[TUCK] === 1 || this.pendingLadderTuck;
    let grab = this.pending[GRAB] === 1;
    let lean = 0;
    let moveX = 0;
    let moveY = 0;
    for (let i = 0; i < SLOTS; i++) {
      const kind = this.kind[i]!;
      if (kind === FREE) continue;
      active = true;
      if (kind === STICK) {
        const dx = this.px[i]! - this.ax[i]!;
        const dy = this.py[i]! - this.ay[i]!;
        const m = Math.hypot(dx, dy);
        const R = cfg.stickRadius;
        const n = Math.min(1, m / R);
        if (m > 0 && n > cfg.stickDeadzone) {
          const v = (n - cfg.stickDeadzone) / (1 - cfg.stickDeadzone);
          moveX = (dx / m) * v;
          moveY = (-dy / m) * v;
        }
      } else if (kind === BODY) {
        const off = this.px[i]! - this.ax[i]!;
        const a = Math.abs(off);
        if (a > cfg.leanDeadzone && this.returning[i] === 0) {
          const v = Math.min(1, (a - cfg.leanDeadzone) / Math.max(1e-6, cfg.leanRadius - cfg.leanDeadzone));
          const l = off < 0 ? -v : v;
          if (Math.abs(l) > Math.abs(lean)) lean = l;
        }
        const h = this.held[i]!;
        if (h === JUMP) jump = true;
        else if (h === TUCK) tuck = true;
        else if (h === GRAB) grab = true;
        if (this.grabbed[i] === 1) grab = true;
        if (this.ladderTuck[i] === 1) tuck = true;
      }
    }
    out.pause = this.pending[3] === 1;
    out.restart = this.pending[4] === 1;
    out.active = active || jump || tuck || grab || out.pause || out.restart;
    out.moveX = moveX;
    out.moveY = moveY;
    out.lean = lean;
    out.reach = grab ? 1 : 0;
    out.jump = jump;
    out.tuck = tuck;
    this.pending.fill(0);
    this.pendingLadderTuck = false;
  }
}
